import type { QueryClient } from '@lumapps/base-api/react-query';
import { sleep } from '@lumapps/utils/time/sleep';
import { queryPredicates } from '@lumapps/widget-base/api';
import { WIDGET_COMMUNITY_POST_LIST_TYPE } from '@lumapps/widget-post-list/constants';
import { invalidateStreamWidgets } from '@lumapps/widget-stream/utils/invalidateStreamWidgets';

import { postQueryKeys } from '../api';

/**
 * Options for invalidating post-related queries
 */
export interface InvalidatePostQueriesOptions {
    /**
     * Delay in milliseconds before invalidating queries.
     * Useful to give the backend time to process and update data.
     * @default 500
     */
    delay?: number;
    /**
     * Whether to invalidate post search queries (drafts, scheduled posts, etc.)
     * @default true
     */
    includeSearchQueries?: boolean;
    /**
     * Whether to invalidate post list widgets
     * @default true
     */
    includePostListWidgets?: boolean;
    /**
     * Whether to invalidate stream widgets (feeds that contain posts, articles, events)
     * @default true
     */
    includeStreamWidgets?: boolean;
    /**
     * Whether to invalidate multi id post queries
     * @default true
     */
    includeMultiPostQueries?: boolean;
}

/**
 * Invalidates post-related queries across the application.
 *
 * This utility provides a centralized way to invalidate various post-related queries
 * with configurable options for different use cases.
 *
 * @param queryClient - The React Query client
 * @param options - Configuration options for what to invalidate and when
 */
export const invalidatePostQueries = async (
    queryClient: QueryClient,
    options: InvalidatePostQueriesOptions = {},
): Promise<void> => {
    const {
        delay = 500,
        includeSearchQueries = true,
        includePostListWidgets = true,
        includeStreamWidgets = true,
        includeMultiPostQueries = true,
    } = options;

    // Wait if delay is specified
    if (delay > 0) {
        await sleep(delay);
    }

    // Invalidate post search queries (drafts, scheduled posts, etc.)
    if (includeSearchQueries) {
        queryClient.invalidateQueries({ queryKey: postQueryKeys.search({}) });
    }

    // Invalidate post list widgets
    if (includePostListWidgets) {
        queryClient.invalidateQueries({
            predicate: queryPredicates.allWidgetsByWidgetType(WIDGET_COMMUNITY_POST_LIST_TYPE),
        });
    }

    // Invalidate stream widgets
    if (includeStreamWidgets) {
        invalidateStreamWidgets(queryClient);
    }

    // Invalidate multi-post queries (individual post fetches for multiple posts)
    if (includeMultiPostQueries) {
        // Prefix matching every `getById` query (['posts', 'post', <id>])
        queryClient.invalidateQueries({ queryKey: ['posts', 'post'] });
    }
};
